import { EventSpeaker } from "@/types/EventSpeaker";
import { Filter } from "@/types/Filter";
import { ConditionGroup } from "@/types/EventRule";
import { api } from "@/lib/api/client";
import { apiPost, apiGet, apiDelete, apiPut } from "../_apiShim";
import { withAiContentTool } from "@/lib/ai/aiContentQuery";

const BASE_PATH = "/api/v1/event-speakers/";

const SPEAKER_FIELDS =
  "speakerId,eventId,name,occupation,bio,imageUrl,socials,sortIndex,createdAt," +
  "company,email,phone,tags,sourceGuestId,sourceContactId," +
  // Wave B structured name parts — legacy-GET readable (BE ALLOWED_FIELDS).
  "firstName,lastName,title,language," +
  // Wave A public attachments (BE ALLOWED_FIELDS mirror).
  "attachments," +
  // Wave D display toggles (bake reads them from the legacy GET).
  "hiddenFields," +
  // publicOnWebsite-gated custom values. The legacy GET used to be forbidden
  // from carrying these at all; the backend now serves only the values whose
  // DEFINITION is published, so an unpublished field still arrives as nothing.
  // The bake needs them to render custom lines on the website speaker card.
  "additionalFields";

/** Admin hub list projection — the ONLY surface that carries custom values
 *  and the promotion link `guestId` (BE LIST_ALLOWED_FIELDS; the legacy GET
 *  deliberately cannot project guestId). */
const SPEAKER_LIST_FIELDS =
  SPEAKER_FIELDS + ",additionalFields,guestId,sponsorId";

export async function createEventSpeaker(
  eventId: string,
  speaker: EventSpeaker,
  /**
   * Art. 50 Abs. 2 KI-VO (J6): the AI lane behind this create — the JSON
   * transfer of a file from an AI-marked (or pre-wave) event sends
   * `template_import`. The body IS the entity, so it travels as
   * `?aiContentTool=`; the server stamps the event's guest-content mark.
   */
  aiContentTool?: string,
): Promise<string> {
  return await apiPost<string>(withAiContentTool(`${BASE_PATH}${eventId}`, aiContentTool), speaker, {
    "Cache-Control": "no-store",
  });
}

/**
 * All speakers of an event. THROWS on failure (see EventProgramClient) so the
 * binding refresher never mistakes an outage for an empty speaker list.
 */
export async function getEventSpeakers(
  eventId: string,
): Promise<EventSpeaker[]> {
  const url = `${BASE_PATH}${eventId}?fields=${encodeURIComponent(SPEAKER_FIELDS)}`;
  return await apiGet<EventSpeaker[]>(url, { "Cache-Control": "no-store" });
}

/**
 * ONE speaker including `additionalFields` (LIST projection).
 *
 * The website builder's speaker popup reuses the hub's editor, but its list
 * comes from the LEGACY projection, which omits custom values on purpose —
 * they must never reach the public bake. With a REQUIRED custom field
 * configured that made the popup's Save dead, and typing a value to clear
 * the error overwrote the stored one. This fetches the full record for the
 * one speaker being edited, leaving the bake's query untouched.
 */
export async function getEventSpeakerDetail(
  eventId: string,
  speakerId: string,
): Promise<EventSpeaker> {
  const url = `${BASE_PATH}${eventId}/${speakerId}/detail?fields=${encodeURIComponent(SPEAKER_LIST_FIELDS)}`;
  return await apiGet<EventSpeaker>(url, { "Cache-Control": "no-store" });
}

export async function updateEventSpeaker(
  eventId: string,
  speakerId: string,
  speaker: Partial<EventSpeaker>,
): Promise<void> {
  await apiPut<string>(`${BASE_PATH}${eventId}/${speakerId}`, speaker, {
    "Cache-Control": "no-store",
  });
}

/** Wave L sponsor↔speaker link: point the speaker at a sponsor org
 *  (overwrite allowed) / clear it. */
export async function linkSpeakerSponsor(
  eventId: string,
  speakerId: string,
  sponsorId: string,
): Promise<void> {
  await apiPost<string>(`${BASE_PATH}${eventId}/${speakerId}/link-sponsor`, {
    sponsorId,
  }, { "Cache-Control": "no-store" });
}

export async function unlinkSpeakerSponsor(
  eventId: string,
  speakerId: string,
): Promise<void> {
  await apiPost<string>(`${BASE_PATH}${eventId}/${speakerId}/unlink-sponsor`, undefined, {
    "Cache-Control": "no-store",
  });
}

export async function deleteEventSpeaker(
  eventId: string,
  speakerId: string,
): Promise<void> {
  await apiDelete<string>(`${BASE_PATH}${eventId}/${speakerId}`, {
    "Cache-Control": "no-store",
  });
}

/**
 * Wave A pinned contract — server-side paginated/filtered speaker list.
 * `POST /api/v1/event-speakers/{eventId}/list`; pageSize capped at 100 by the
 * resource (`pageSizeExceeded`), default sort `sortIndex asc, name asc`.
 * Returns speakers INCLUDING `additionalFields` (admin-only surface).
 */
export async function listEventSpeakersPaginated(
  eventId: string,
  params: {
    searchTerm?: string;
    filters?: Filter[];
    conditions?: ConditionGroup;
    page: number;
    pageSize: number;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  },
): Promise<EventSpeaker[]> {
  return await apiPost<EventSpeaker[]>(
    `${BASE_PATH}${eventId}/list`,
    { fields: SPEAKER_LIST_FIELDS, ...params },
    { "Cache-Control": "no-store" },
    true,
  );
}

/** Wave A pinned contract — count twin of the list endpoint. */
export async function countEventSpeakers(
  eventId: string,
  params: {
    searchTerm?: string;
    filters?: Filter[];
    conditions?: ConditionGroup;
  },
): Promise<number> {
  return await apiPost<number>(
    `${BASE_PATH}${eventId}/count`,
    params,
    { "Cache-Control": "no-store" },
    true,
  );
}

/**
 * Wave A pinned contract — synchronous speaker export (csv|excel), mirroring
 * the check-in-log export resource idiom. Filter/search-scoped (the contract
 * carries no row-id selection). Returns the file as a Blob.
 */
export async function exportEventSpeakers(
  eventId: string,
  params: {
    format: "csv" | "excel";
    fields?: string;
    searchTerm?: string;
    filters?: Filter[];
    conditions?: ConditionGroup;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    columnLabels?: Record<string, string>;
  },
): Promise<Blob> {
  const r = await api.post(`${BASE_PATH}${eventId}/export`, params, {
    responseType: "blob",
    headers: { "Cache-Control": "no-store" },
  });
  return r.data as Blob;
}

/** 200 body of the promote endpoint (EventSpeakerPromotionService.PromoteResult):
 *  `linked` is true on every 200; `existing` = the link already existed
 *  (idempotent repeat / half-link heal); `created` = a new guest row was
 *  minted this call. */
export type PromoteSpeakerResult = {
  guestId: string;
  linked: boolean;
  existing: boolean;
  created: boolean;
};

/**
 * Wave B — promote a speaker to an event guest ("also attends").
 * `POST /api/v1/event-speakers/{eventId}/{speakerId}/promote`, body
 * `{attachGuestId?, createNew?}` — explicit-guestId-first flow (2026-07-23):
 * `attachGuestId` (hex) attaches that guest and wins over `createNew`;
 * `createNew: true` mints a new guest WITHOUT email matching (duplicate
 * email = explicit intent); neither → legacy server-side email
 * dedupe-or-create. Caps: event-speakers:write + guests:write (imperative,
 * server-side). Failure codes: 409 `multipleGuestMatches` (envelope carries
 * `candidates[]` — retry with `attachGuestId`; unreachable from the picker
 * flow, kept as a safety net), 409 `guestAlreadyLinked` / `eventAtCapacity`
 * (promotion never auto-waitlists), 400 `guestNotFound`.
 */
export async function promoteEventSpeaker(
  eventId: string,
  speakerId: string,
  opts?: { attachGuestId?: string; createNew?: boolean },
): Promise<PromoteSpeakerResult> {
  return await apiPost<PromoteSpeakerResult>(
    `${BASE_PATH}${eventId}/${speakerId}/promote`,
    {
      ...(opts?.attachGuestId ? { attachGuestId: opts.attachGuestId } : {}),
      ...(opts?.createNew ? { createNew: true } : {}),
    },
    { "Cache-Control": "no-store" },
  );
}

/**
 * Import a speaker prefilled from an event guest or a CRM contact. Exactly
 * one of guestId/contactId. Guest path additionally requires guests:read
 * (checked server-side); the photo is BYTE-COPIED into public event content.
 * Returns the new speaker id.
 */
export async function importEventSpeaker(
  eventId: string,
  source: { guestId?: string; contactId?: string; includePhoto?: boolean },
): Promise<string> {
  return await apiPost<string>(`${BASE_PATH}${eventId}/import`, source, {
    "Cache-Control": "no-store",
  });
}
